/**
 * DEC-001: Shopify-style Experience View for Item Price DocType
 * 
 * This view creates a Shopify-like item price detail page with a Details tab.
 * 
 * Details Tab Sections:
 * - item-price-overview: Item price identification and key information
 * - item-price-details: Core item price details (price list, rate, currency, UOM)
 * - item-price-validity: Validity period information
 * - item-price-context: Context-specific fields (customer, supplier, batch)
 * - status-summary: Item price status information
 */

import type { DocTypeView } from '../types';

export const itemPriceShopifyView: DocTypeView = {
  id: 'item-price-shopify-v1',
  doctype: 'Item Price',
  style: 'shopify',
  context: undefined,
  status: 'active',
  label: 'Item Price – Shopify Style',
  description: 'Shopify-style layout for Item Price detail page with Details tab',
  formView: {
    sections: [
      // 1. Basic Pricing Information (all core pricing fields in one group)
      {
        id: 'item-price-basic',
        title: 'Basic Information',
        description: 'Item price identification and pricing details',
        layout: [
          {
            columns: [
              {
                fieldname: 'name',
                mode: 'read',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'item_code',
                mode: 'read',
                visibility: 'default',
                width: '2/3',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'item_name',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'brand',
                mode: 'read',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'packing_unit',
                mode: 'read',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'description',
                mode: 'read',
                visibility: 'default',
                width: 'full',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'price_list',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'currency',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'price_list_rate',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'uom',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
        ],
      },

      // 3. Type & Validity (buying/selling and validity period)
      {
        id: 'item-price-type-validity',
        title: 'Type & Validity',
        description: 'Price type and validity period',
        layout: [
          {
            columns: [
              {
                fieldname: 'buying',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'selling',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
          {
            columns: [
              {
                fieldname: 'valid_from',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
              {
                fieldname: 'valid_upto',
                mode: 'edit',
                visibility: 'default',
                width: '1/2',
              },
            ],
          },
        ],
      },

      // 4. Context (customer, supplier, batch)
      {
        id: 'item-price-context',
        title: 'Context',
        description: 'Context-specific pricing information',
        layout: [
          {
            columns: [
              {
                fieldname: 'customer',
                mode: 'edit',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'supplier',
                mode: 'edit',
                visibility: 'default',
                width: '1/3',
              },
              {
                fieldname: 'batch_no',
                mode: 'edit',
                visibility: 'default',
                width: '1/3',
              },
            ],
          },
        ],
      },
    ],
    customFieldBucket: {
      enabled: true,
      sectionTitle: 'Additional ERP Fields',
      visibilityDefault: 'collapsed',
    },
  },
  createdBy: 'system',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

